import { Backdrop, Dialog, DialogProps } from '@mui/material';
import { uniqueId } from 'lodash';
import React, { useCallback, useState } from 'react';
import Draggable from 'react-draggable';

import { useSxStyles } from '~/components/Dialogs/components/DraggableDialog/useStyles';
import { mergeSxProps } from '~/utils/helpers/sxProps';

type DraggableDialogProps = DialogProps & {
  showPortalBars?: boolean;
  disableBackdropClick?: boolean;
  allowClickThroughBackdrop?: boolean;
};

export const DraggableDialog = (props: DraggableDialogProps) => {
  const {
    children,
    showPortalBars,
    disableBackdropClick,
    allowClickThroughBackdrop,
    ...dialogProps
  } = props;
  const sxStyles = useSxStyles();

  const [dialogId] = useState(uniqueId('draggable-dialog-'));

  const handleOnClose: DialogProps['onClose'] = useCallback(
    (event, reason) => {
      if (disableBackdropClick && reason && reason === 'backdropClick') {
        // Prevent closing the dialog, when clicking outside the dialog
        return;
      }

      if (!!dialogProps?.onClose) {
        dialogProps.onClose(event, reason);
      }
    },
    [disableBackdropClick, dialogProps],
  );

  if (dialogProps.fullScreen) {
    return (
      <Dialog
        {...dialogProps}
        sx={mergeSxProps(
          dialogProps.sx,
          showPortalBars ? sxStyles.showPortalBarsOnFullScreen : undefined,
        )}
      >
        {children}
      </Dialog>
    );
  }

  return (
    <Backdrop
      open={!dialogProps.hideBackdrop && dialogProps.open}
      sx={sxStyles.backdrop}
    >
      <Draggable
        handle={`#${dialogId}`}
        cancel={'[class*="MuiDialogContent-root"]'}
      >
        <Dialog
          {...dialogProps}
          sx={mergeSxProps(
            dialogProps.sx,
            allowClickThroughBackdrop
              ? sxStyles.clickThroughBackdrop
              : undefined,
          )}
          PaperProps={{
            ...dialogProps.PaperProps,
            sx: mergeSxProps(
              dialogProps.PaperProps?.sx,
              allowClickThroughBackdrop
                ? sxStyles.clickThroughBackdropNotPaper
                : undefined,
            ),
          }}
          onClose={handleOnClose}
          hideBackdrop={true}
          aria-labelledby={dialogId}
        >
          {children}
        </Dialog>
      </Draggable>
    </Backdrop>
  );
};
